import { declinesSessionWindow } from "../constants";
import { useEffect, useMemo, useState } from "react";
import type { ConfiguratorSharedProps } from "../../widgetDefinition";
import { useSelector, useDispatch } from "react-redux";
import { Checkbox, Collapse, Input, Space, Tag, Typography } from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import type { MetricOption, PlayerNotesComponentSpec, SubjectEntity } from "../../Processing/types";
import { getTeamPlayerNotes } from "@store/actions/player.actions";
import { resolveDateFilterRange } from "@utils/notesDates.mjs";
import { buildDateFilter } from "../../Processing/buildQueries";
import { ConfigPanelLayout } from "../shared/ConfigPanelLayout";
import { SlicerTokenInput } from "../shared/SlicerTokenInput";
import { PreviewShell } from "../shared/PreviewShell";
import { DateRangeForm } from "../shared/DateRangeForm";
import { resolveDateRange } from "../../Utils/resolveDateRange";
import { PlayersForm } from "../shared/PlayersForm";
import { useConfiguratorSync } from "../shared/useConfiguratorSync";
import { ToggleOption } from "../shared/ToggleOption";
import { isSectionBindable } from "../../Processing/controllerBindings";
import { SectionSummary } from "../shared/SectionSummary";
import {
  playerNotesReducer,
  createInitialState,
  hydrateFromSpec,
  formStateToSpec,
  type PlayerNotesFormState,
} from "./playerNotesState";

const { Panel } = Collapse;
const { Text } = Typography;

const EMPTY_METRICS: MetricOption[] = [];

// ── Preview helpers ──────────────────────────────────────────

const DATE_RANGE_OFFSETS: Record<string, [number, number, number]> = {
  today:         [0,   0,   0],
  last1week:     [0,   3,   6],
  last2weeks:    [1,   7,  13],
  last4weeks:    [2,  14,  26],
  last3months:   [5,  45,  85],
  lastSeason:    [10, 120, 300],
};

function getSampleDates(dateRange: string): string[] {
  const today = dayjs();
  const [a, b, c] = DATE_RANGE_OFFSETS[dateRange] ?? [2, 14, 26];
  const raw = [
    today.subtract(a, "day").format("MMM D"),
    today.subtract(b, "day").format("MMM D"),
    today.subtract(c, "day").format("MMM D"),
  ];
  return [...new Set(raw)];
}

function getSamplePlayerNames(
  selectedSubjects: string[],
  getSubjectEntities: (t: string) => SubjectEntity[],
): string[] {
  const specificPlayers = selectedSubjects.filter((s) => s.startsWith("player-"));
  if (specificPlayers.length > 0) {
    const allPlayers = getSubjectEntities("player");
    const names = specificPlayers
      .map((id) => allPlayers.find((p) => `player-${p.id}` === id)?.name)
      .filter(Boolean) as string[];
    if (names.length > 0) return names.slice(0, 3);
  }
  const players = getSubjectEntities("player");
  if (players.length > 0) return players.slice(0, 3).map((p) => p.name);
  return ["Alex Johnson", "Sam Martinez", "Jordan Lee"];
}

function getNoteText(
  noteId: string | null,
  searchPattern: string,
  realNoteText: string | undefined,
  playerName: string,
  index: number,
): string {
  if (noteId) {
    if (realNoteText) {
      return realNoteText.slice(0, 140) + (realNoteText.length > 140 ? "…" : "");
    }
    return `Sample player note for ID #${noteId}. Content will be retrieved from the database matching this note record.`;
  }
  if (searchPattern) {
    const templates = [
      `Training review: ${playerName} demonstrated "${searchPattern}" tendencies during the recent session — flagged for coaching follow-up.`,
      `Session notes: observed "${searchPattern}" in today's performance data for ${playerName}. Monitoring closely.`,
      `Wellness check: "${searchPattern}" was noted during check-in with ${playerName}. Further assessment recommended.`,
    ];
    return templates[index % templates.length];
  }
  const generic = [
    `${playerName} completed full training session. Load within target range. Total distance and high-speed running within weekly norms.`,
    `Recovery session today. ${playerName} reported feeling fresh. Wellness score 8/10. No concerns flagged.`,
    `High-intensity day for ${playerName}. Total distance above weekly average. Monitoring fatigue through next session.`,
  ];
  return generic[index % generic.length];
}

type StoredNote = {
  player_notes_id: string;
  notes: string;
  player_name?: string | null;
  author_name?: string | null;
  timestamp?: string | null;
};

const SAMPLE_AUTHORS = ["Coach Williams", "Dr. Patel", "Coach Roberts"];
const EXAMPLE_PLAYER_NAMES = ["Marcus Johnson", "Emma Williams", "David Chen"];

function buildPreviewEntries(
  state: PlayerNotesFormState,
  getSubjectEntities: (t: string) => SubjectEntity[],
  storedNotes: StoredNote[],
): Array<{ player: string; date: string; text: string; author: string }> {
  const dates = getSampleDates(state.dateRange);
  const hasNoteIds = state.noteIds.length > 0;

  if (hasNoteIds) {
    const selectedNotes = state.noteIds
      .map((id) => storedNotes.find((n) => n.player_notes_id === id))
      .filter(Boolean) as StoredNote[];

    if (selectedNotes.length > 0) {
      // If search pattern, prefer notes that contain it; fall back to all selected
      let notesToShow = selectedNotes;
      if (state.searchPattern) {
        const q = state.searchPattern.toLowerCase();
        const matching = selectedNotes.filter((n) => n.notes.toLowerCase().includes(q));
        if (matching.length > 0) notesToShow = matching;
      }
      // Use real player names when specific notes are selected
      return notesToShow.map((note, i) => ({
        player: note.player_name ?? "Player",
        author: note.author_name ?? "Unknown",
        date: note.timestamp ? dayjs(note.timestamp).format("MMM D") : (dates[i % dates.length] ?? dates[0]),
        text: note.notes.length > 140 ? note.notes.slice(0, 140) + "…" : note.notes,
      }));
    }
  }

  // Fallback: use fixed example names (no specific notes selected)
  return Array.from({ length: 3 }, (_, i) => ({
    player: EXAMPLE_PLAYER_NAMES[i % EXAMPLE_PLAYER_NAMES.length],
    author: SAMPLE_AUTHORS[i % SAMPLE_AUTHORS.length],
    date: dates[i % dates.length] ?? dates[0],
    text: getNoteText(null, state.searchPattern, undefined, EXAMPLE_PLAYER_NAMES[i % EXAMPLE_PLAYER_NAMES.length], i),
  }));
}

// ── Component ────────────────────────────────────────────────


export function PlayerNotesConfigurator({
  onSpecChange,
  existingSpec,
  getSubjectEntities,
}: ConfiguratorSharedProps<PlayerNotesComponentSpec>) {
  const reduxDispatch = useDispatch();
  const playerNotes: StoredNote[] = useSelector(({ player }: any) => player?.playerNotes ?? []);
  const timeZone: string = useSelector(({ team }: any) => team.info.home_timezone);

  const [activeSubTab, setActiveSubTab] = useState<"notes" | "search">("notes");
  const [notesSearchFilter, setNotesSearchFilter] = useState("");

  const [state, dispatch] = useConfiguratorSync({
    reducer: playerNotesReducer,
    existingSpec,
    hydrate: hydrateFromSpec,
    createInitial: createInitialState,
    toSpec: formStateToSpec,
    onSpecChange,
    metrics: EMPTY_METRICS,
    getSubjectEntities,
  });

  // The picker lists the notes of the date range that the widget saves.
  const range = resolveDateFilterRange(
    buildDateFilter(formStateToSpec(state, EMPTY_METRICS).player_notes_builder_misc ?? {}),
    timeZone,
  );
  const from = range?.from;
  const to = range?.to;

  useEffect(() => {
    reduxDispatch(getTeamPlayerNotes(from, to) as any);
  }, [reduxDispatch, from, to]);

  const dateLabel = resolveDateRange(state);
  const totalSelected = state.selectedSubjects.filter((s) => s !== "full-team").length;

  const allNoteIds = useMemo(() => playerNotes.map((n) => n.player_notes_id), [playerNotes]);
  const allSelected = allNoteIds.length > 0 && allNoteIds.every((id) => state.noteIds.includes(id));
  const someSelected = state.noteIds.length > 0 && !allSelected;

  const filteredPlayerNotes = useMemo(() => {
    if (!notesSearchFilter.trim()) return playerNotes;
    const query = notesSearchFilter.toLowerCase();
    return playerNotes.filter((note) => {
      const playerName = (note.player_name ?? "").toLowerCase();
      const noteText = (note.notes ?? "").toLowerCase();
      return playerName.includes(query) || noteText.includes(query);
    });
  }, [playerNotes, notesSearchFilter]);

  const toggleNote = (id: string) => {
    const next = state.noteIds.includes(id)
      ? state.noteIds.filter((x) => x !== id)
      : [...state.noteIds, id];
    dispatch({ type: "SET_NOTE_IDS", noteIds: next });
  };

  const handleSelectAll = (checked: boolean) =>
    dispatch({ type: "SET_NOTE_IDS", noteIds: checked ? allNoteIds : [] });

  const previewEntries = useMemo(
    () => buildPreviewEntries(state, getSubjectEntities, playerNotes),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [state.noteIds, state.searchPattern, state.dateRange, state.selectedSubjects, getSubjectEntities, playerNotes],
  );

  const hasAnyFilter = state.noteIds.length > 0 || !!state.searchPattern;

  const form = (
    <>
    <div style={{ padding: "12px 16px", borderBottom: "1px solid #e8ecf0" }}>
      <Space size={8} align="center" style={{ width: "100%" }}>
        <Text style={{ fontSize: 12, minWidth: 40 }}>Title</Text>
        <SlicerTokenInput
          size="small"
          placeholder="e.g. Player Notes"
          value={state.label}
          onChange={(label) => dispatch({ type: "SET_LABEL", label })}
          style={{ flex: 1 }}
        />
      </Space>
    </div>
    <Collapse
      accordion
      activeKey={state.expandedKey}
      onChange={(key) => dispatch({ type: "SET_EXPANDED_KEY", key })}
      style={{ borderRadius: 0, borderTop: "none" }}
    >
      <Panel
        key="__date_range"
        header={
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span className="awm-step-number">1</span>
            <Text strong style={{ fontSize: 13 }}>Date Range</Text>
            <SectionSummary kind="date" controllerId={state.dateControllerId} own={dateLabel} />
          </div>
        }
      >
        <DateRangeForm
          keepsOwnDatesOnLatestN={declinesSessionWindow("player_notes")}
          bindable={isSectionBindable("player_notes", "date")}
          value={state}
          onChange={(patch) => dispatch({ type: "UPDATE_DATE_RANGE", patch })}
        />
      </Panel>

      <Panel
        key="__players"
        header={
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span className="awm-step-number">2</span>
            <Text strong style={{ fontSize: 13 }}>Filters</Text>
            <SectionSummary kind="subject" controllerId={state.subjectControllerId}
              own={totalSelected > 0 ? `${totalSelected} selected` : "Full Team"}
              getSubjectEntities={getSubjectEntities} />
          </div>
        }
      >
        <div className="awm-step-body">
          <PlayersForm
            bindable={isSectionBindable("player_notes", "subject")}
            value={state}
            onChange={(patch) => dispatch({ type: "UPDATE_PLAYERS", patch })}
            getSubjectEntities={getSubjectEntities}
            hideDrills
          />
          <ToggleOption
            label="Exclude Injured Players"
            description="Hide injured athletes' rows."
            checked={state.excludeInjuredPlayers}
            onChange={(v) => dispatch({ type: "SET_EXCLUDE_INJURED", value: v })}
          />
        </div>
      </Panel>

      <Panel
        key="__options"
        header={
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span className="awm-step-number">3</span>
            <Text strong style={{ fontSize: 13 }}>Advanced Filters</Text>
            {(() => {
              const count = (state.noteIds.length > 0 ? 1 : 0) + (state.searchPattern ? 1 : 0);
              return count > 0 ? <Text type="secondary" style={{ fontSize: 11 }}>{count}</Text> : null;
            })()}
          </div>
        }
      >
        <div className="awm-step-body">
          {/* Tab switcher */}
          <Space size={4} wrap>
            <Tag.CheckableTag
              checked={activeSubTab === "notes"}
              onChange={() => setActiveSubTab("notes")}
            >
              Individual Notes{state.noteIds.length > 0 ? ` (${state.noteIds.length})` : ""}
            </Tag.CheckableTag>
            <Tag.CheckableTag
              checked={activeSubTab === "search"}
              onChange={() => setActiveSubTab("search")}
            >
              Filter by Text{state.searchPattern ? " •" : ""}
            </Tag.CheckableTag>
          </Space>

          {/* Individual Notes content */}
          {activeSubTab === "notes" && (
            <div className="awm-sub-section">
              <div className="awm-sub-section-header">
                <Checkbox
                  checked={allSelected}
                  indeterminate={someSelected}
                  onClick={(e) => e.stopPropagation()}
                  onChange={(e) => handleSelectAll(e.target.checked)}
                />
                <span style={{ fontSize: 12, fontWeight: 500 }}>Select All</span>
                <Input
                  size="small"
                  placeholder="Search notes..."
                  value={notesSearchFilter}
                  onChange={(e) => setNotesSearchFilter(e.target.value)}
                  allowClear
                  style={{ flex: 1, marginLeft: 8, maxWidth: 200 }}
                />
                {state.noteIds.length > 0 && (
                  <span className="awm-sub-badge">{state.noteIds.length} selected</span>
                )}
              </div>
              <div className="awm-sub-section-body">
                {filteredPlayerNotes.length === 0 ? (
                  <div style={{ padding: "8px 10px", fontSize: 12, color: "#8c8c8c" }}>No notes available</div>
                ) : (
                  filteredPlayerNotes.map((note) => {
                    const id = note.player_notes_id;
                    const checked = state.noteIds.includes(id);
                    const noteName = note.player_name ?? null;
                    const noteDate = note.timestamp ? dayjs(note.timestamp).format("MMM D") : null;
                    return (
                      <div
                        key={id}
                        className={`awm-note-row${checked ? " awm-note-row--selected" : ""}`}
                        onClick={() => toggleNote(id)}
                      >
                        <Checkbox
                          checked={checked}
                          onClick={(e) => e.stopPropagation()}
                          onChange={() => toggleNote(id)}
                        />
                        <div className="awm-note-row-content">
                          <div className="awm-note-row-meta">
                            {noteName && <span className="awm-note-player">{noteName}</span>}
                            {noteDate && <span className="awm-note-date">{noteDate}</span>}
                          </div>
                          <div className="awm-note-text">{note.notes}</div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          )}

          {/* Filter by Text content */}
          {activeSubTab === "search" && (
            <Input
              size="small"
              placeholder="Search inside notes…"
              value={state.searchPattern}
              onChange={(e) => dispatch({ type: "SET_SEARCH_PATTERN", pattern: e.target.value })}
              allowClear
            />
          )}
        </div>
      </Panel>
    </Collapse>
    </>
  );

  const preview = (
    <PreviewShell
      title={state.label || "Player Notes"}
      titleAddon={<InfoCircleOutlined style={{ fontSize: 13, color: "rgba(0,0,0,0.35)" }} />}
      hasContent
    >
      {hasAnyFilter && (
        <Text type="secondary" style={{ fontSize: 11, fontStyle: "italic", textAlign: "center", display: "block", marginBottom: 8, padding: "0 4px" }}>
          Final notes depend on data availability in the selected date range and applied filters.
        </Text>
      )}
      <div className="player-notes-preview-list">
        {previewEntries.map((entry, i) => (
          <div key={i} className="player-notes-preview-entry">
            <div className="player-notes-preview-entry-header">
              <span className="player-notes-header-left">
                <span className="player-notes-preview-player-badge">{entry.player}</span>
                <span className="player-notes-preview-date-tag">{entry.date}</span>
              </span>
              <span className="player-notes-preview-author">{entry.author}</span>
            </div>
            <div className="player-notes-preview-note-text">{entry.text}</div>
          </div>
        ))}
      </div>
    </PreviewShell>
  );

  return <ConfigPanelLayout form={form} preview={preview} />;
}
